// src/components/common/ShareModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  X, Share2, Copy, Check, LinkIcon, Send, Facebook, Twitter, MessageCircle, 
  Mail, Linkedin, QrCode, Download, Eye, Users, Globe, Lock, Building2,
  ExternalLink, Palette, BookOpen, Store, FileText, Settings, User,
  Camera, Calendar, Award, Briefcase, Package, Play, Image, Heart
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

// Unified content type configuration
const CONTENT_TYPE_CONFIG = {
  // Books
  education: {
    icon: BookOpen,
    color: 'blue',
    shareText: 'Check out this interactive learning material',
    features: ['Interactive lessons', 'Progress tracking', 'Quizzes & assessments'],
    type: 'book'
  },
  marketing: {
    icon: Users,
    color: 'green', 
    shareText: 'Discover this engaging marketing experience',
    features: ['Lead capture', 'Analytics tracking', 'Social engagement'],
    type: 'book'
  },
  brochure: {
    icon: Building2,
    color: 'purple',
    shareText: 'Explore this professional brochure',
    features: ['Product showcase', 'Company information', 'Contact integration'],
    type: 'book'
  },
  catalog: {
    icon: Store,
    color: 'orange',
    shareText: 'Browse this interactive product catalog',
    features: ['Product filtering', 'Search functionality', 'Item management'],
    type: 'book'
  },
  
  // Albums
  'photo-album': {
    icon: Camera,
    color: 'pink',
    shareText: 'View this beautiful photo album',
    features: ['Photo galleries', 'Slideshow mode', 'Media organization'],
    type: 'album'
  },
  'menu': {
    icon: Building2,
    color: 'amber',
    shareText: 'Browse our delicious menu',
    features: ['Dish details', 'Pricing', 'Categories & filtering'],
    type: 'album'
  },
  'listing': {
    icon: Building2,
    color: 'blue',
    shareText: 'Explore this property listing',
    features: ['Property details', 'Location info', 'Contact options'],
    type: 'album'
  },
  'store': {
    icon: Store,
    color: 'purple',
    shareText: 'Shop our product catalog',
    features: ['Product catalog', 'Shopping features', 'Secure checkout'],
    type: 'album'
  },
  'showcase': {
    icon: Award,
    color: 'red',
    shareText: 'View this impressive showcase',
    features: ['Portfolio display', 'Project details', 'Professional presentation'],
    type: 'album'
  },
  
  // Profiles
  'personal': {
    icon: User,
    color: 'blue',
    shareText: 'Connect with this professional',
    features: ['Professional background', 'Skills & experience', 'Contact information'],
    type: 'profile'
  },
  'product': {
    icon: Package,
    color: 'green',
    shareText: 'Discover this amazing product',
    features: ['Product features', 'Specifications', 'User testimonials'],
    type: 'profile'
  },
  'service': {
    icon: Briefcase,
    color: 'purple',
    shareText: 'Learn about our services',
    features: ['Service offerings', 'Pricing', 'Client testimonials'],
    type: 'profile'
  },
  'organization': {
    icon: Building2,
    color: 'indigo',
    shareText: 'Learn about our organization',
    features: ['Company information', 'Team members', 'Services & values'],
    type: 'profile'
  },
  'project': {
    icon: Play,
    color: 'orange',
    shareText: 'Explore this project',
    features: ['Project details', 'Features', 'Team & progress'],
    type: 'profile'
  },
  'asset': {
    icon: Image,
    color: 'teal',
    shareText: 'View this digital asset',
    features: ['Asset details', 'Media gallery', 'Information & specs'],
    type: 'profile'
  },
  
  // Events
  'conference': {
    icon: Calendar,
    color: 'blue',
    shareText: 'Join this exciting conference',
    features: ['Event schedule', 'Speaker lineup', 'Registration'],
    type: 'event'
  },
  'workshop': {
    icon: Award,
    color: 'green',
    shareText: 'Attend this valuable workshop',
    features: ['Learning objectives', 'Instructor info', 'Registration'],
    type: 'event'
  },
  'webinar': {
    icon: Play,
    color: 'purple',
    shareText: 'Register for this informative webinar',
    features: ['Online attendance', 'Expert speakers', 'Interactive Q&A'],
    type: 'event'
  },
  'meetup': {
    icon: Users,
    color: 'orange',
    shareText: 'Join our community meetup',
    features: ['Networking opportunities', 'Local venue', 'Community building'],
    type: 'event'
  },
  'party': {
    icon: Heart,
    color: 'pink',
    shareText: 'Come celebrate with us',
    features: ['Fun activities', 'Food & drinks', 'Great company'],
    type: 'event'
  },
  
  // Fallback
  'general': {
    icon: FileText,
    color: 'gray',
    shareText: 'Check out this interactive content',
    features: ['Flexible content', 'Custom layouts', 'Multi-media support'],
    type: 'general'
  }
};

export const ShareModal = ({ 
  content, 
  contentType = 'page', // 'book', 'album', 'profile', 'event'
  isOpen, 
  onClose, 
  trackingEnabled = false 
}) => {
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [embedCode, setEmbedCode] = useState('');
  const [activeTab, setActiveTab] = useState('link');
  const [showQR, setShowQR] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const qrCodeRef = useRef();

  // Determine content configuration
  const getContentConfig = () => {
    if (!content) return CONTENT_TYPE_CONFIG.general;
    
    // Try to get category from various possible fields
    const category = content.category || 
                    content.botCategory?.toLowerCase() || 
                    content.subCategory || 
                    content.eventCategory || 
                    content.albumCategory ||
                    'general';
    
    return CONTENT_TYPE_CONFIG[category] || CONTENT_TYPE_CONFIG.general;
  };

  const contentConfig = getContentConfig();

  // Get content name from various possible fields
  const getContentName = () => {
    return content?.botName || 
           content?.name || 
           content?.title || 
           content?.eventName ||
           content?.albumName ||
           'Untitled Content';
  };

  const getContentDescription = () => {
    return content?.description || 
           content?.botTask || 
           content?.summary ||
           content?.eventDescription ||
           contentConfig.shareText;
  };

  // Construct share URL with customPath support
  useEffect(() => {
    if (content) {
      let baseUrl;
      
      if (content.customDomain) {
        // Custom domain with optional custom path
        baseUrl = `https://${content.customDomain}${content.customPath ? `/${content.customPath}` : ''}`;
      } else if (content.customPath) {
        // Custom path on main domain
        baseUrl = `${window.location.origin}/${content.customPath}`;
      } else {
        // Default URL structure based on content type
        const routeMap = {
          book: 'books',
          album: 'albums', 
          profile: 'profiles',
          event: 'events'
        };
        const route = routeMap[contentType] || 'books';
        baseUrl = `${window.location.origin}/${route}/${content.id}`;
      }
      
      // Add tracking parameters if enabled
      const trackingParams = trackingEnabled ? 
        `?utm_source=share&utm_medium=direct&utm_campaign=${content.id}&utm_content=${contentType}` : '';
      
      const finalUrl = `${baseUrl}${trackingParams}`;
      setShareUrl(finalUrl);
      
      // Set default custom message
      setCustomMessage(contentConfig.shareText);
      
      // Update embed code
      setEmbedCode(
        `<iframe src="${finalUrl}" width="100%" height="600" frameborder="0" allowfullscreen></iframe>`
      );
    }
  }, [content, contentType, trackingEnabled]);

  const handleCopy = async (text, type = 'link') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(type);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: getContentName(),
          text: customMessage || getContentDescription(),
          url: shareUrl
        });
        
        // Track native sharing
        if (trackingEnabled && window.gtag) {
          window.gtag('event', 'share', {
            method: 'native',
            content_type: contentType,
            content_id: content.id
          });
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
          handleCopy(shareUrl); // Fallback to copy
        }
      }
    } else {
      handleCopy(shareUrl); // Fallback to copy
    }
  };

  const shareToSocial = (platform) => {
    const title = encodeURIComponent(getContentName());
    const text = encodeURIComponent(customMessage || getContentDescription());
    const url = encodeURIComponent(shareUrl);
    const hashtags = contentType === 'book' ? 'interactive,education' : 
                    contentType === 'album' ? 'gallery,photos' :
                    contentType === 'profile' ? 'professional,networking' :
                    contentType === 'event' ? 'events,meetup' : 'interactive';

    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}&hashtags=${hashtags}`,
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      email: `mailto:?subject=${title}&body=${text}%0A%0A${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}&summary=${text}`,
      telegram: `https://t.me/share/url?url=${url}&text=${text}`
    };

    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400,scrollbars=yes,resizable=yes');
      
      // Track social sharing
      if (trackingEnabled && window.gtag) {
        window.gtag('event', 'share', {
          method: platform,
          content_type: contentType,
          content_id: content.id
        });
      }
    }
  };

  const downloadQRCode = () => {
    const svg = qrCodeRef.current.querySelector('svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      
      const link = document.createElement('a');
      link.download = `${getContentName().replace(/[^a-z0-9]/gi, '_').toLowerCase()}-qr-code.png`;
      link.href = canvas.toDataURL();
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const getAccessLevelInfo = () => {
    const level = content?.accessLevel || content?.access || 'public';
    const configs = {
      public: { icon: Globe, text: 'Public', color: 'text-green-600 bg-green-50', description: 'Anyone can access' },
      private: { icon: Lock, text: 'Private', color: 'text-red-600 bg-red-50', description: 'Login required' },
      organization: { icon: Building2, text: 'Organization', color: 'text-blue-600 bg-blue-50', description: 'Organization members only' }
    };
    return configs[level] || configs.public;
  };

  if (!isOpen) return null;

  const accessInfo = getAccessLevelInfo();
  const AccessIcon = accessInfo.icon;
  const ContentIcon = contentConfig.icon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-${contentConfig.color}-100`}>
              <Share2 className={`h-5 w-5 text-${contentConfig.color}-600`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Share {contentType.charAt(0).toUpperCase() + contentType.slice(1)}</h2>
              <p className="text-sm text-gray-600 flex items-center gap-2">
                <ContentIcon className="h-4 w-4" />
                {getContentName()}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Content Preview */}
          <div className="p-6 border-b">
            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="relative">
                {content?.ogImage || content?.logoUrl || content?.heroImage || content?.profileImageUrl ? (
                  <img
                    src={content.ogImage || content.logoUrl || content.heroImage || content.profileImageUrl}
                    alt={getContentName()}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                ) : (
                  <div className={`w-16 h-16 rounded-lg bg-${contentConfig.color}-100 flex items-center justify-center`}>
                    <ContentIcon className={`h-8 w-8 text-${contentConfig.color}-600`} />
                  </div>
                )}
                <div className={`absolute -bottom-1 -right-1 px-2 py-1 rounded-full text-xs font-medium ${accessInfo.color}`}>
                  <AccessIcon className="h-3 w-3 inline mr-1" />
                  {accessInfo.text}
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate">{getContentName()}</h3>
                <p className="text-sm text-gray-600 mb-2 line-clamp-2">{getContentDescription()}</p>
                
                {/* Features */}
                <div className="flex flex-wrap gap-1">
                  {contentConfig.features.slice(0, 3).map((feature, index) => (
                    <span key={index} className={`px-2 py-1 bg-${contentConfig.color}-100 text-${contentConfig.color}-700 text-xs rounded-full`}>
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b">
            <div className="flex">
              {[
                { id: 'link', label: 'Share Link', icon: LinkIcon },
                { id: 'social', label: 'Social Media', icon: Send },
                { id: 'embed', label: 'Embed Code', icon: Settings },
                { id: 'qr', label: 'QR Code', icon: QrCode }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? `border-${contentConfig.color}-500 text-${contentConfig.color}-600 bg-${contentConfig.color}-50`
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  <span className="hidden sm:inline text-sm">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Link Tab */}
            {activeTab === 'link' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Message
                  </label>
                  <textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    rows="2"
                    placeholder="Add a custom message to share with your link..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Share URL
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={shareUrl}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => handleCopy(shareUrl, 'link')}
                      className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                        copied === 'link'
                          ? 'bg-green-500 text-white' 
                          : `bg-${contentConfig.color}-600 text-white hover:bg-${contentConfig.color}-700`
                      }`}
                    >
                      {copied === 'link' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      {copied === 'link' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>

                {navigator.share && (
                  <button
                    onClick={handleNativeShare}
                    className={`w-full py-3 bg-${contentConfig.color}-600 text-white rounded-lg hover:bg-${contentConfig.color}-700 transition-colors flex items-center justify-center gap-2 font-medium`}
                  >
                    <Send className="h-4 w-4" />
                    Share via Device
                  </button>
                )}
              </div>
            )}

            {/* Social Tab */}
            {activeTab === 'social' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">Share on your favorite platforms:</p>
                
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { platform: 'facebook', label: 'Facebook', icon: Facebook, color: 'bg-blue-600 hover:bg-blue-700' },
                    { platform: 'twitter', label: 'Twitter', icon: Twitter, color: 'bg-black hover:bg-gray-800' },
                    { platform: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: 'bg-blue-700 hover:bg-blue-800' },
                    { platform: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'bg-green-600 hover:bg-green-700' },
                    { platform: 'telegram', label: 'Telegram', icon: Send, color: 'bg-blue-500 hover:bg-blue-600' },
                    { platform: 'email', label: 'Email', icon: Mail, color: 'bg-gray-600 hover:bg-gray-700' }
                  ].map(({ platform, label, icon: Icon, color }) => (
                    <button
                      key={platform}
                      onClick={() => shareToSocial(platform)}
                      className={`flex items-center justify-center gap-3 px-4 py-3 text-white rounded-lg transition-colors ${color}`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="font-medium">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Embed Tab */}
            {activeTab === 'embed' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Embed Code
                  </label>
                  <div className="relative">
                    <textarea
                      value={embedCode}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      rows="3"
                    />
                    <button
                      onClick={() => handleCopy(embedCode, 'embed')}
                      className={`absolute top-2 right-2 px-3 py-1 rounded text-sm font-medium transition-all ${
                        copied === 'embed'
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {copied === 'embed' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
                
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="text-sm font-medium text-amber-800 mb-2">Embedding Guidelines:</h4>
                  <ul className="text-xs text-amber-700 space-y-1">
                    <li>• Paste this code into your website's HTML</li>
                    <li>• Adjust width and height as needed</li>
                    <li>• Ensure your site allows iframes</li>
                    <li>• Test on different screen sizes</li>
                  </ul>
                </div>
              </div>
            )}

            {/* QR Code Tab */}
            {activeTab === 'qr' && (
              <div className="space-y-4">
                <div className="text-center">
                  <div className="inline-block p-4 bg-white border border-gray-200 rounded-lg shadow-sm" ref={qrCodeRef}>
                    <QRCodeSVG 
                      value={shareUrl}
                      size={200}
                      bgColor="#ffffff"
                      fgColor="#000000"
                      level="M"
                      includeMargin={true}
                    />
                  </div>
                  
                  <p className="text-sm text-gray-600 mt-4 mb-6">
                    Scan this QR code to access the {contentType} directly
                  </p>
                  
                  <button
                    onClick={downloadQRCode}
                    className={`inline-flex items-center gap-2 px-4 py-2 bg-${contentConfig.color}-600 text-white rounded-lg hover:bg-${contentConfig.color}-700 transition-colors font-medium`}
                  >
                    <Download className="h-4 w-4" />
                    Download QR Code
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};